import { CarWheelIdentifiers } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { BookingStoreCar, BrandType, NoddiAsyncError, WheelStorageTempType } from 'noddi-types';
import {
  CountryCode,
  CountryCodeType,
  getAcceptedLicensePlateNumberLengths,
  isValidLicensePlateNumber,
  vehicleCategoryFromRecord
} from 'noddi-util';
import { RefObject, useRef, useState } from 'react';

export interface FoundCar extends BookingStoreCar {
  id: number;
}

type Options = {
  countryCode?: CountryCodeType;
  selectedLicensePlateNumbers?: string[];
  brandDomain?: BrandType;
};

export type RegNumberSearchState = {
  countryCode: CountryCodeType;
  regNumber: string;
  onRegNumberChange: (input: string) => void;
  inputRef: RefObject<HTMLInputElement | null>;
  /** A complete, valid plate is typed and no lookup is running or has failed for it. */
  canSubmit: boolean;
  isSearching: boolean;
  isInvalid: boolean;
  alreadyExists: boolean;
  error: NoddiAsyncError | null;
  /** Looks up the typed plate; resolves the car and clears the input, or `undefined` on a miss or duplicate. */
  search: () => Promise<FoundCar | undefined>;
};

export const useRegNumberSearch = ({
  countryCode = CountryCode.NORWAY,
  selectedLicensePlateNumbers,
  brandDomain
}: Options): RegNumberSearchState => {
  const [regNumber, setRegNumber] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [isInvalid, setIsInvalid] = useState(false);
  const [alreadyExists, setAlreadyExists] = useState(false);

  const { error, refetch, isLoading, isRefetching } = noddiAsync.useGet({
    type: URLKeys.getCarFromLicensePlateNumber,
    input: { number: regNumber, countryCode, brandDomains: brandDomain ? [brandDomain] : undefined },
    queryConfig: {
      enabled: false
    }
  });

  const search = async () => {
    setAlreadyExists(false);
    const result = await refetch();
    const fetched = result.data;
    if (!fetched) {
      return undefined;
    }

    const isDuplicate = selectedLicensePlateNumbers?.some(
      (n) => n.toUpperCase() === fetched.licensePlateNumber.toUpperCase()
    );

    if (isDuplicate) {
      setAlreadyExists(true);
      return undefined;
    }
    // Map StoredCarWheelSetRecordForNewBooking[] to WheelStorageTempType[]
    const wheelStorage: WheelStorageTempType[] | undefined = fetched.wheelStorage?.map((ws) => ({
      carWheelSet: {
        id: ws.carWheelSet.id,
        createdAt: ws.carWheelSet.createdAt || '',
        updatedAt: ws.carWheelSet.updatedAt || '',
        tireSeason: ws.carWheelSet.tireSeason
      },
      storage: {
        leadTimeInSeconds: ws.storage.leadTimeInSeconds
      },
      userGroupId: ws.userGroupId
    }));

    setRegNumber('');
    inputRef.current?.focus(); // Refocus input after successful add

    return {
      make: fetched.makeName,
      model: fetched.model,
      licensePlate: { number: fetched.licensePlateNumber, countryCode: fetched.countryCode },
      vehicleCategory: vehicleCategoryFromRecord(fetched),
      wheelStorage,
      hasTireHotel: !!wheelStorage?.length,
      hasStuddedWinterTires: fetched.carWheelIdentifiers?.identifiers?.includes(CarWheelIdentifiers.IS_STUDDED),
      image: fetched.image,
      makeLogo: fetched.makeLogo,
      displayName: fetched.displayName,
      id: fetched.id
    };
  };

  const onRegNumberChange = (input: string) => {
    setIsInvalid(!isValidLicensePlateNumber({ input, countryCode }));
    setAlreadyExists(false); // Reset already exists error when user types
    setRegNumber(input);
  };

  const isSearching = isLoading || isRefetching;
  const isRegNumberLengthInvalid = !getAcceptedLicensePlateNumberLengths({ countryCode }).includes(regNumber.length);

  return {
    countryCode,
    regNumber,
    onRegNumberChange,
    inputRef,
    canSubmit: !isRegNumberLengthInvalid && !isInvalid && !isSearching && !error && !alreadyExists,
    isSearching,
    isInvalid,
    alreadyExists,
    error,
    search
  };
};
